import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminUsers, useSuspendUser, useDeleteUser, usePromoteToAdmin } from '@/hooks/useAdminUsers';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { UserX, Trash2, Shield, Eye, UserPlus } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

const AdminUsers = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<{
    role: string;
    status: 'active' | 'suspended' | 'deleted' | '';
    search: string;
  }>({
    role: '',
    status: '',
    search: '',
  });
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [actionDialog, setActionDialog] = useState<{ type: 'suspend' | 'delete' | 'promote' | null; user: any }>({ type: null, user: null });

  const { data: users, isLoading } = useAdminUsers({
    role: filters.role || undefined,
    status: filters.status || undefined,
    includeDeleted: showDeleted,
  });
  const { data: deletedUsers } = useAdminUsers({ status: 'deleted' });
  const { mutate: suspendUser } = useSuspendUser();
  const { mutate: deleteUser } = useDeleteUser();
  const { mutate: promoteToAdmin } = usePromoteToAdmin();

  const filteredUsers = users?.filter(user => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        user.email.toLowerCase().includes(searchLower) ||
        user.first_name?.toLowerCase().includes(searchLower) ||
        user.last_name?.toLowerCase().includes(searchLower) ||
        user.company?.name?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const handleAction = () => {
    if (!actionDialog.user) return;
    
    switch (actionDialog.type) {
      case 'suspend':
        suspendUser(actionDialog.user.id);
        break;
      case 'delete':
        deleteUser(actionDialog.user.id);
        break;
      case 'promote':
        promoteToAdmin(actionDialog.user.id);
        break;
    }
    setActionDialog({ type: null, user: null });
  };

  const getStatusBadge = (isActive: boolean, deletedAt?: string | null) => {
    if (deletedAt) {
      return <Badge className="bg-[#5A6B7A] text-white">{t('admin.users.statuses.deleted')}</Badge>;
    }
    if (isActive) {
      return <Badge className="bg-[#14A38B] text-white">{t('admin.users.statuses.active')}</Badge>;
    }
    return <Badge className="bg-[#FF7171] text-white">{t('admin.users.statuses.suspended')}</Badge>;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold text-[#37699E] font-['Lexend_Deca']">{t('admin.users.title')}</h1>
            <p className="text-sm text-muted-foreground mt-2">{t('admin.users.subtitle')}</p>
          </div>
          <Button asChild className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold rounded-lg">
            <Link to="/admin/create">
              <UserPlus className="w-4 h-4 mr-2" />
              {t('adminCreate.navShort')}
            </Link>
          </Button>
        </div>

        <Card className="p-6">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <SearchInput
              placeholder={t('common.search')}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
            <Select value={filters.role || undefined} onValueChange={(v) => setFilters({ ...filters, role: v })}>
              <SelectTrigger>
                <SelectValue placeholder={t('admin.users.filters.allRoles')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="shipper">{t('common.shipper')}</SelectItem>
                <SelectItem value="transporter">{t('common.transporter')}</SelectItem>
                <SelectItem value="driver">{t('common.driver')}</SelectItem>
                <SelectItem value="admin">{t('common.admin')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.status || undefined} onValueChange={(v) => setFilters({ ...filters, status: v as 'active' | 'suspended' | 'deleted' | '' })}>
              <SelectTrigger>
                <SelectValue placeholder={t('admin.users.filters.allStatuses')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t('admin.users.statuses.active')}</SelectItem>
                <SelectItem value="suspended">{t('admin.users.statuses.suspended')}</SelectItem>
                <SelectItem value="deleted">{t('admin.users.statuses.deleted')}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => { setFilters({ role: '', status: '', search: '' }); setShowDeleted(false); }}>
              {t('common.reset')}
            </Button>
          </div>

          <div className="flex items-center gap-3 mb-6">
            <Switch
              id="show-deleted"
              checked={showDeleted || filters.status === 'deleted'}
              disabled={filters.status === 'deleted'}
              onCheckedChange={setShowDeleted}
            />
            <label htmlFor="show-deleted" className="text-sm text-[#2D3748] cursor-pointer font-['Funnel_Sans']">
              {t('admin.users.showDeleted')}
            </label>
            {!showDeleted && filters.status !== 'deleted' && (deletedUsers?.length ?? 0) > 0 && (
              <span className="text-xs text-[#5A6B7A]">
                {t('admin.users.deletedHiddenCount', { count: deletedUsers?.length ?? 0 })}
              </span>
            )}
          </div>


          {/* Table */}
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.users.name')}</TableHead>
                    <TableHead>{t('admin.users.email')}</TableHead>
                    <TableHead>{t('admin.users.company')}</TableHead>
                    <TableHead>{t('admin.users.role')}</TableHead>
                    <TableHead>{t('admin.users.status')}</TableHead>
                    <TableHead>{t('admin.users.registrationDate')}</TableHead>
                    <TableHead className="text-right">{t('admin.users.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers?.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {user.first_name} {user.last_name}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {user.company ? (
                          <div className="flex flex-col">
                            <span>{user.company.name}</span>
                            {user.company.founded_date && (
                              <span className="text-xs text-[#5A6B7A]">
                                {t('admin.users.companyDetails.companyCreatedSince', { date: format(new Date(user.company.founded_date), 'dd/MM/yyyy') })}
                              </span>
                            )}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{user.role}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(user.is_active, user.deleted_at)}</TableCell>
                      <TableCell>{format(new Date(user.created_at), 'dd/MM/yyyy')}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedUser(user)}
                          >
                            <Eye size={16} />
                          </Button>
                          {user.is_active && !user.deleted_at && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setActionDialog({ type: 'suspend', user })}
                            >
                              <UserX size={16} className="text-[#F2AC57]" />
                            </Button>
                          )}
                          {user.role !== 'admin' && !user.deleted_at && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setActionDialog({ type: 'promote', user })}
                            >
                              <Shield size={16} className="text-[#0880AE]" />
                            </Button>
                          )}
                          {!user.deleted_at && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setActionDialog({ type: 'delete', user })}
                            >
                              <Trash2 size={16} className="text-[#FF7171]" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      {/* User Details Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('admin.users.userDetails.title')}</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">{t('admin.users.userDetails.personalInfo')}</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">{t('admin.users.name')}</p>
                    <p className="font-medium">{selectedUser.first_name} {selectedUser.last_name}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('admin.users.email')}</p>
                    <p className="font-medium">{selectedUser.email}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('admin.users.userDetails.phone')}</p>
                    <p className="font-medium">{selectedUser.phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('admin.users.role')}</p>
                    <Badge variant="outline" className="capitalize">{selectedUser.role}</Badge>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('admin.users.status')}</p>
                    {getStatusBadge(selectedUser.is_active, selectedUser.deleted_at)}
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('admin.users.registrationDate')}</p>
                    <p className="font-medium">{format(new Date(selectedUser.created_at), 'dd/MM/yyyy')}</p>
                  </div>
                </div>
              </div>

              {selectedUser.company && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">{t('admin.users.userDetails.companyInfo')}</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">{t('admin.users.company')}</p>
                      <p className="font-medium">{selectedUser.company.name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('admin.users.companyDetails.type')}</p>
                      <Badge variant="outline" className="capitalize">{selectedUser.company.type}</Badge>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('admin.users.companyDetails.createdAt')}</p>
                      <p className="font-medium">
                        {selectedUser.company.founded_date
                          ? format(new Date(selectedUser.company.founded_date), 'dd/MM/yyyy')
                          : format(new Date(selectedUser.company.created_at), 'dd/MM/yyyy')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <AlertDialog open={!!actionDialog.type} onOpenChange={() => setActionDialog({ type: null, user: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionDialog.type === 'suspend' && t('admin.users.confirmSuspend')}
              {actionDialog.type === 'delete' && t('admin.users.confirmDelete')}
              {actionDialog.type === 'promote' && t('admin.users.confirmPromote')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionDialog.type === 'suspend' && t('admin.users.confirmSuspendMessage')}
              {actionDialog.type === 'delete' && t('admin.users.confirmDeleteMessage')}
              {actionDialog.type === 'promote' && t('admin.users.confirmPromoteMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleAction}>{t('common.confirm')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminUsers;
